import { toCamelCase } from "@estaly/ui";
import StoresService from "@/api/services/stores";

const initialState = {
  index: [],
  currentPage: 1,
  totalPages: 0,
  totalCount: 0,
};

export default {
  namespaced: true,
  state: initialState,
  getters: {
    getIndex(state) {
      return state.index;
    },
    getCurrentPage(state) {
      return state.currentPage;
    },
    getTotalPages(state) {
      return state.totalPages;
    },
    getTotalCount(state) {
      return state.totalCount;
    },
    getCategories: () => () => {
      return ["A", "B", "C"];
    },
    getRetributionPayoutMethods: () => () => {
      return ["estaly_gift_card", "store_salary"];
    },
    getGiftCardProviders: () => () => {
      return ["edenred", "other"];
    },
    getRevenueRanges: () => () => {
      return ["0_to_250k", "250k_to_1M", "1M_to_5M", "5M_to_10M", "above_10M"];
    },
    getOwnershipTypes: () => () => {
      return ["branch", "association", "franchisee", "independant"];
    },
    getRegions: () => () => [
      { value: "IDF", label: "IDF" },
      { value: "Centre", label: "Centre" },
      { value: "Nord-Est", label: "Nord-Est" },
      { value: "Nord-Ouest", label: "Nord-Ouest" },
      { value: "Sud-Est", label: "Sud-Est" },
      { value: "Sud-Ouest", label: "Sud-Ouest" },
      { value: "DOM-TOM", label: "DOM-TOM" },
    ],

    getStatusStyle: () => (status) => {
      switch (status) {
        case "setup":
          return "bg-yellow-200 text-yellow-800";
        case "active":
          return "bg-green-200 text-green-800";
        case "inactive":
          return "bg-red-200 text-red-600";
        default:
          return null;
      }
    },
    isStoreSetup: () => (store) => {
      return store?.status === "setup";
    },
    isPendingValidation: () => (store, configField) => {
      return store?.[configField] === "pending_validation";
    },
    storeStatusOrSetup: () => (store) => {
      return store?.status === "active" || store?.status === "inactive" ? store.status : store?.setupStatus;
    },
    getStoreStatusFieldDisplayStyle: () => (status) => {
      switch (status) {
        case "active":
          return "bg-green-100 text-green-800";
        case "inactive":
          return "bg-red-100 text-red-800";
        case "pending_verification":
          return "bg-blue-100 text-blue-800";
        case "information_to_be_completed":
          return "bg-yellow-100 text-yellow-800";
        default:
          return "bg-yellow-100 text-yellow-800";
      }
    },
    getRemunerationModels: () => () => {
      return ["classic", "advance_commission_without_max_churn", "advance_commission_with_max_churn"];
    },
    getReferentRoles: () => () => {
      return ["store_manager", "director", "franchise_owner"];
    },
    getStatuses: () => () => {
      return ["active", "inactive", "information_to_be_completed", "pending_verification"];
    },
    getSourceStyle: () => (source) => {
      switch (source) {
        case "shopify":
          return "bg-green-100 text-green-800";
        case "offline":
          return "bg-gray-100 text-gray-800";
        case "woo_commerce":
          return "bg-purple-100 text-purple-800";
        case "prestashop":
          return "bg-blue-100 text-blue-800";
        case "magento2":
        case "magento1":
          return "bg-orange-100 text-orange-800";
        case "api":
          return "bg-yellow-100 text-yellow-800";
        default:
          return "bg-gray-100 text-gray-800";
      }
    },
    allowedToCreateMandate: (_, getters) => (store) => {
      return !store?.mandate && getters.effectiveInvoiceLevel(store) === "store";
    },

    effectiveInvoiceLevel: () => (store) => {
      const level = store?.organization?.invoiceLevel;
      if (level === "billing_entity" && !store?.billingEntity) return "store";
      return level;
    },
    isStoreOffline: () => (store) => {
      return store?.source === "offline";
    },
  },
  mutations: {
    setIndex(state, data) {
      state.index = data.records;
    },
    setPagination(state, { currentPage, totalPages, totalCount }) {
      state.currentPage = currentPage;
      state.totalPages = totalPages;
      state.totalCount = totalCount;
    },
  },
  actions: {
    async fetch(_, { id, includes }) {
      return await StoresService.fetch(id, { includes });
    },

    async fetchIndex(
      { commit },
      {
        page = null,
        keywords = null,
        organizationId = [],
        merchantProfileId = null,
        contractDistributionId = null,
        perPage = null,
        invoiceResponsible = null,
        status = [],
        source = [],
        includes = [],
        order = null,
        region = [],
      } = {},
    ) {
      const response = await StoresService.fetchIndex({
        page,
        keywords,
        organizationId,
        merchantProfileId,
        contractDistributionId,
        perPage,
        invoiceResponsible,
        status,
        source,
        includes,
        order,
        region,
      });

      if (!response?.success) return response;

      const { data } = response;
      const formattedData = toCamelCase(data);
      commit("setIndex", formattedData);
      commit("setPagination", {
        currentPage: formattedData.page.currentPage,
        totalPages: formattedData.page.totalPages,
        totalCount: formattedData.page.totalCount,
      });
      return formattedData;
    },

    async updateGeneralInformations(
      _,
      { storeId, businessName, salesChannel, billingAddress, revenueRange, shippingAddress, ownershipType },
    ) {
      return await StoresService.updateGeneralInformations(storeId, {
        businessName,
        salesChannel,
        billingAddress,
        revenueRange,
        shippingAddress,
        ownershipType,
      });
    },

    async updateContactInformations(_, { storeId, email, referentRole, referent }) {
      return await StoresService.updateContactInformations(storeId, { email, referentRole, referent });
    },

    async updateAfterSalesService(_, { storeId, repairWorkshop, repairWorkshopEmail }) {
      return await StoresService.updateAfterSalesService(storeId, { repairWorkshop, repairWorkshopEmail });
    },

    async update(_, { storeId, data }) {
      return await StoresService.update(storeId, data);
    },
    async configureStore(_, storeParams) {
      return await StoresService.configureStore(storeParams);
    },
    async updateConfigurationStatus(_, { storeId, step, status }) {
      return await StoresService.updateConfigurationStatus(storeId, step, status);
    },
    async updateStatus(_, { storeId, status }) {
      return await StoresService.updateStatus(storeId, status);
    },
    async updateChallenge(_, { storeId, challengeId }) {
      return await StoresService.updateChallenge(storeId, challengeId);
    },
    async updateDiscount(_, { storeId, discountId }) {
      return await StoresService.updateDiscount(storeId, discountId);
    },
  },
};
